import { useMemo, useState } from 'react';
import { addDays, addMonths, isAfter, isBefore, isSameDay, isSameMonth, startOfMonth } from 'date-fns';
import Icon from './Icon';
import styles from './Calendar.module.css';

export type CalendarProps = {
  /** 'range' seleciona início e fim no próprio calendário; 'unica' seleciona um dia só. */
  modo?: 'range' | 'unica';
  inicio?: Date | null;
  fim?: Date | null;
  onChange: (inicio: Date, fim?: Date) => void;
};

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

const normalizar = (data: Date) => new Date(data.getFullYear(), data.getMonth(), data.getDate());

/**
 * Calendário do design system — substitui os inputs de data crus do navegador.
 * No modo range: primeiro clique marca o início, segundo marca o fim (invertendo
 * se necessário); o intervalo é pré-visualizado ao passar o mouse.
 */
export default function Calendar({ modo = 'unica', inicio, fim, onChange }: CalendarProps) {
  const base = inicio ?? new Date();
  const [mesVisivel, setMesVisivel] = useState<Date>(startOfMonth(base));
  const [pendente, setPendente] = useState<Date | null>(null);
  const [pairando, setPairando] = useState<Date | null>(null);

  const celulas = useMemo(() => {
    const primeiro = startOfMonth(mesVisivel);
    const inicioGrade = addDays(primeiro, -primeiro.getDay());
    return Array.from({ length: 42 }, (_, indice) => addDays(inicioGrade, indice));
  }, [mesVisivel]);

  const tituloMes = mesVisivel
    .toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    .replace(' de ', ' ');

  const hoje = normalizar(new Date());

  const selecionar = (dia: Date) => {
    const data = normalizar(dia);
    if (modo === 'unica') {
      onChange(data);
      return;
    }
    if (!pendente) {
      setPendente(data);
      setPairando(null);
      return;
    }
    const inicioFinal = isBefore(data, pendente) ? data : pendente;
    const fimFinal = isBefore(data, pendente) ? pendente : data;
    setPendente(null);
    setPairando(null);
    onChange(inicioFinal, fimFinal);
  };

  // Intervalo exibido: o range confirmado ou a prévia (pendente → cursor).
  const faixaInicio = pendente ?? (inicio ? normalizar(inicio) : null);
  const faixaFim = pendente ? pairando : fim ? normalizar(fim) : null;
  const [faixaA, faixaB] =
    faixaInicio && faixaFim && isAfter(faixaInicio, faixaFim) ? [faixaFim, faixaInicio] : [faixaInicio, faixaFim];

  const classesDoDia = (dia: Date) => {
    const classes = [styles.dia];
    if (!isSameMonth(dia, mesVisivel)) classes.push(styles.foraDoMes);
    if (isSameDay(dia, hoje)) classes.push(styles.hoje);

    const ehInicio = faixaA && isSameDay(dia, faixaA);
    const ehFim = faixaB && isSameDay(dia, faixaB);
    const noMeio = faixaA && faixaB && isAfter(dia, faixaA) && isBefore(dia, faixaB);

    if (modo === 'unica') {
      if (inicio && isSameDay(dia, inicio)) classes.push(styles.selecionado);
    } else {
      if (ehInicio || ehFim) classes.push(styles.selecionado);
      if (noMeio) classes.push(styles.noIntervalo);
      if (ehInicio && faixaB && !isSameDay(faixaA as Date, faixaB)) classes.push(styles.pontaInicio);
      if (ehFim && faixaA && !isSameDay(faixaA, faixaB as Date)) classes.push(styles.pontaFim);
    }
    return classes.join(' ');
  };

  return (
    <div className={styles.calendario}>
      <div className={styles.cabecalho}>
        <button type="button" className={styles.navegacao} onClick={() => setMesVisivel(addMonths(mesVisivel, -12))} aria-label="Ano anterior">
          <Icon nome="keyboard_double_arrow_left" tamanho={16} />
        </button>
        <button type="button" className={styles.navegacao} onClick={() => setMesVisivel(addMonths(mesVisivel, -1))} aria-label="Mês anterior">
          <Icon nome="chevron_left" tamanho={18} />
        </button>
        <span className={styles.mesAtual}>{tituloMes}</span>
        <button type="button" className={styles.navegacao} onClick={() => setMesVisivel(addMonths(mesVisivel, 1))} aria-label="Próximo mês">
          <Icon nome="chevron_right" tamanho={18} />
        </button>
        <button type="button" className={styles.navegacao} onClick={() => setMesVisivel(addMonths(mesVisivel, 12))} aria-label="Próximo ano">
          <Icon nome="keyboard_double_arrow_right" tamanho={16} />
        </button>
      </div>
      <div className={styles.semana}>
        {DIAS_SEMANA.map((dia) => (
          <span key={dia} className={styles.diaSemana}>
            {dia}
          </span>
        ))}
      </div>
      <div className={styles.grade} onMouseLeave={() => setPairando(null)}>
        {celulas.map((dia) => (
          <button
            key={dia.toISOString()}
            type="button"
            className={classesDoDia(dia)}
            onClick={() => selecionar(dia)}
            onMouseEnter={() => pendente && setPairando(normalizar(dia))}
          >
            {dia.getDate()}
          </button>
        ))}
      </div>
      {modo === 'range' && (
        <div className={styles.rodape}>
          {pendente
            ? `Início: ${pendente.toLocaleDateString('pt-BR')}. Selecione a data final`
            : 'Clique no dia inicial e depois no final'}
        </div>
      )}
    </div>
  );
}
